import { useQuery } from '@apollo/client'
import { Hidden, useMediaQuery, useTheme } from '@material-ui/core'
import Pagination from '@material-ui/lab/Pagination'
import { matchSorter } from 'match-sorter'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import { ACCOMMODATION_NAME_QUERY } from '../../graphql/accommodation'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import roomTypePagesRoleManagement from '../../staticRoleManagement/roomTypeRoleManagement'
import { ROLE } from '../../staticRoleManagement/types'
import { RoomTypeBrief, RoomTypeFilters } from '../../types/roomtype'
import RoomtypesHeader from './RoomtypesHeader'
import { RoomtypesTable } from './RoomtypesTable'
import { RoomtypesTableMobile } from './RoomtypesTableMobile'
import useStyles from './styles/index.styles'

const RoomtypesList: React.FC = () => {
  const classes = useStyles()
  const theme = useTheme()
  const params = useParams<{ accommodationID: string; propertyCode: string }>()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [perPage] = useState(5)
  const [page, setPage] = useState(1)
  const [count, setcount] = useState<number>(0)
  const [searchInputValue, setsearchInputValue] = useState<string>('')
  const [filters, setFilters] = useState<RoomTypeFilters>({
    type: 'all',
    existing: 'all',
    activation: 'all'
  })
  const [filteredRoomTypes, setfilteredRoomTypes] = useState<RoomTypeBrief[]>(
    []
  )

  const numberOfPages = useMemo(() => Math.ceil(count / perPage), [count])

  const hasAccessToViewRoomTypeTabs = roomTypePagesRoleManagement
    .find(
      (item) =>
        item.route ===
        '/accommodations/:accommodationID/:propertyCode/room-types/list'
    )
    ?.rolesListWithReadAccess?.includes(getRoleFromStorage())

  const isRoleAdmin = getRoleFromStorage() === ROLE.ADMIN

  const {
    data: roomTypeData,
    loading,
    refetch
  } = useQuery<{
    roomTypes: RoomTypeBrief[]
  }>(ROOMTYPES_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      propertyCode: params.propertyCode,
      showDeletedRoomTypes: isRoleAdmin ? true : false,
      showDeactivedRoomTypes: true,
      showLog: true
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const { data: accommodationData, loading: accnameLoading } = useQuery<{
    accommodationName: {
      name: string
    }
  }>(ACCOMMODATION_NAME_QUERY, {
    variables: {
      propertyCode: params.propertyCode
    }
  })

  useEffect(() => {
    setPage(1)
    const fullRoomtypesList = roomTypeData ? roomTypeData?.roomTypes : []
    const newFilteredRoomtypes = matchSorter(
      fullRoomtypesList,
      searchInputValue,
      {
        keys: ['name', 'displayRoomTypeCode', 'type'],
        sorter: (rankedItems) => rankedItems
      }
    )

    setfilteredRoomTypes(newFilteredRoomtypes)
    setcount(newFilteredRoomtypes.length)
  }, [searchInputValue, loading])

  useEffect(() => {
    if (roomTypeData) {
      setPage(1)
      const { type, existing, activation } = filters

      const tempList = roomTypeData?.roomTypes.filter((item) => {
        // Filter by type
        if (type === 'virtual' && item.type !== 'Virtual') return false
        if (type === 'physical' && item.type !== 'Physical') return false

        // Filter by existing status
        if (existing === 'deleted' && !item.deleted) return false
        if (existing === 'existing' && item.deleted) return false

        // Filter by activation status
        if (activation === 'deactive' && item.active) return false
        if (activation === 'active' && !item.active) return false

        // If all conditions are passed, keep the item
        return true
      })

      setfilteredRoomTypes(tempList)
      setcount(tempList.length)
    }
  }, [filters, loading])

  return (
    <div>
      <PageHeader title={getTranslate('RoomType')} />
      <RoomtypesHeader
        inputValue={searchInputValue}
        setinputVal={setsearchInputValue}
        accommodationName={accommodationData?.accommodationName.name || ''}
        loading={accnameLoading}
        filters={filters}
        setFilters={setFilters}
        isRoleAdmin={isRoleAdmin}
      />
      <Hidden smDown>
        <RoomtypesTable
          data={filteredRoomTypes.slice((page - 1) * perPage, page * perPage)}
          hasAccessToViewRoomTypeTabs={hasAccessToViewRoomTypeTabs}
          loading={loading}
          reloadData={refetch}
          isRoleAdmin={isRoleAdmin}
          setFilters={setFilters}
        />
      </Hidden>
      <Hidden mdUp>
        <RoomtypesTableMobile
          hasAccessToViewRoomTypeTabs={hasAccessToViewRoomTypeTabs}
          loading={loading}
          data={filteredRoomTypes.slice((page - 1) * perPage, page * perPage)}
        />
      </Hidden>
      <div className={classes.paginationContainer}>
        <Pagination
          count={numberOfPages}
          color="primary"
          size={isSmallScreen ? 'small' : 'medium'}
          page={page}
          onChange={(_, value) => setPage(value)}
        />
      </div>
    </div>
  )
}

export default RoomtypesList
